import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, track } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useOutputLang } from "../context/OutputLangContext";
import { LanguageToggle } from "../components/LanguageToggle";
import { PaisleyDivider, LotusMark } from "../components/Motif";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Bookmark, Share2, Download, ArrowRight, Quote, Sparkles, TrendingUp, Clock, Orbit, CheckCircle2, AlertTriangle, Scale } from "lucide-react";

const STR = {
  en: { exec: "Executive summary", overall: "Your overall theme", chart: "Your birth-chart structure",
    why: "Why your chart shows this", maha: "Current Mahadasha", antar: "Current Antardasha",
    dashaTl: "Your dasha timeline", transitsT: "Current transits", periodsT: "Stronger & demanding periods",
    supportive: "More supportive", transition: "Transition", demanding: "More demanding",
    timeline: "Your 12–36 month timeline", change: "What could change the outcome",
    technical: "Technical astrology", summary: "Your reading in 5 points", meaning: "What this could mean for you",
    reflection: "Reflection & action", next: "What would you like to explore next?", save: "Save", share: "Share",
    explain: "Show technical details", from: "From ₹", saved: "Saved to My Readings", activates: "Activates",
    opportunity: "Opportunity", caution: "Caution", upcoming: "Upcoming", rules: "Rules houses",
    signin: "Sign in to keep this reading in My Readings.", yogas: "Yogas in your chart" },
  hi: { exec: "सार", overall: "आपका समग्र विषय", chart: "आपकी जन्म-कुंडली संरचना",
    why: "आपकी कुंडली ऐसा क्यों दिखाती है", maha: "वर्तमान महादशा", antar: "वर्तमान अंतरदशा",
    dashaTl: "आपकी दशा समयरेखा", transitsT: "वर्तमान गोचर", periodsT: "अनुकूल और चुनौतीपूर्ण दौर",
    supportive: "अधिक अनुकूल", transition: "संक्रमण", demanding: "अधिक चुनौतीपूर्ण",
    timeline: "आपकी 12–36 महीने की समयरेखा", change: "परिणाम को क्या बदल सकता है",
    technical: "तकनीकी ज्योतिष", summary: "5 बिंदुओं में आपकी रीडिंग", meaning: "इसका आपके लिए क्या अर्थ हो सकता है",
    reflection: "चिंतन और कदम", next: "आगे क्या जानना चाहेंगे?", save: "सेव", share: "शेयर",
    explain: "तकनीकी विवरण देखें", from: "₹", saved: "रीडिंग सेव हो गई", activates: "सक्रिय करता है",
    opportunity: "अवसर", caution: "सावधानी", upcoming: "आगामी", rules: "स्वामी भाव",
    signin: "इस रीडिंग को सेव रखने के लिए साइन इन करें।", yogas: "आपकी कुंडली के योग" },
};

export default function Reading() {
  const { readingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang } = useOutputLang();
  const [r, setR] = useState(null);
  const [share, setShare] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/readings/${readingId}`).then(({ data }) => {
      if (!data.paid) return navigate(`/teaser/${readingId}`, { replace: true });
      setR(data);
      track("full_reading_viewed", { reading_id: readingId, tool_id: data.tool_id });
    }).catch(() => navigate("/"));
  }, [readingId]); // eslint-disable-line

  const s = STR[lang];
  const saveReading = async () => {
    if (!user) return toast(lang === "hi" ? "सेव करने के लिए खाता बनाएं" : "Create an account to save", { description: s.signin });
    try { await api.post(`/readings/${readingId}/save`); toast.success(s.saved); } catch { toast.error("Could not save"); }
  };
  const openShare = async () => {
    track("share_clicked", { reading_id: readingId });
    try { const { data } = await api.get(`/readings/${readingId}/share`); setShare(data); setShareOpen(true); }
    catch { toast.error("Could not create share card"); }
  };
  const crossSell = (c) => { track("cross_sell_clicked", { from: r.tool_id, to: c.tool_id }); navigate(`/ask/${c.tool_id}`); };

  if (!r) return <div className="mx-auto max-w-2xl px-5 py-20"><div className="h-96 rounded-2xl bg-muted shimmer" /></div>;
  const c = (lang === "hi" && r.content_hi) ? r.content_hi : r.content;
  const bp = r.birth_profile || {};
  const ex = c.exec_summary || {};

  return (
    <div className="mx-auto max-w-2xl px-5 pt-8 md:pt-12">
      <div className="mb-4 flex justify-end"><LanguageToggle /></div>

      <div className="relative overflow-hidden rounded-2xl bg-foreground p-7 text-background">
        <LotusMark className="absolute -right-3 -top-3 h-28 w-28 text-background/10" />
        <div className="inline-flex items-center gap-2 rounded-full bg-background/10 px-3 py-1 text-xs"><LotusMark className="h-3.5 w-3.5" /> {r.tool_title}</div>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight md:text-4xl" data-testid="reading-title">{c.theme_title || r.category}</h1>
        <p className="mt-2 text-background/70">"{r.question}"</p>
        {c.phase && <div className="mt-3 inline-block rounded-full bg-gold/25 px-3 py-1 text-sm font-medium">{c.phase}</div>}
        {(bp.dob || bp.birthplace) && (
          <p className="mt-4 text-xs text-background/60">{bp.dob || ""}{bp.birth_time ? ` • ${bp.birth_time}` : ""}{bp.birthplace ? ` • ${bp.birthplace.city}` : ""}</p>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <Button data-testid="save-reading" onClick={saveReading} variant="outline" className="flex-1 rounded-full btn-lift"><Bookmark className="mr-2 h-4 w-4" /> {s.save}</Button>
        <Button data-testid="share-reading" onClick={openShare} variant="outline" className="flex-1 rounded-full btn-lift"><Share2 className="mr-2 h-4 w-4" /> {s.share}</Button>
        <Button data-testid="pdf-reading" onClick={() => window.print()} variant="outline" className="flex-1 rounded-full btn-lift"><Download className="mr-2 h-4 w-4" /> PDF</Button>
      </div>

      {/* Executive summary */}
      {(ex.theme || ex.opportunity) && (
        <Section icon={Sparkles} title={s.exec} testid="section-exec">
          <div className="card-soft mt-3 rounded-2xl p-5">
            {ex.theme && <p className="text-[15px] leading-relaxed">{ex.theme}</p>}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {ex.opportunity && <Mini label={s.opportunity} text={ex.opportunity} tone="good" />}
              {ex.caution && <Mini label={s.caution} text={ex.caution} tone="warn" />}
            </div>
            {ex.upcoming && <p className="mt-3 text-sm text-muted-foreground"><span className="font-medium text-foreground">{s.upcoming}: </span>{ex.upcoming}</p>}
          </div>
        </Section>
      )}

      {/* Simple content theme paragraphs (numerology/tarot/compatibility) */}
      {(c.theme_paragraphs || []).length > 0 && (
        <Section title={s.overall} testid="section-theme">
          {c.theme_paragraphs.map((p, i) => <p key={i} className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{p}</p>)}
        </Section>
      )}

      {/* Chart structure */}
      {(c.chart_structure || []).length > 0 && (
        <><PaisleyDivider />
        <Section icon={Orbit} title={s.chart} testid="section-chart">
          <div className="mt-3 space-y-2.5">
            {c.chart_structure.map((row, i) => (
              <div key={i} className="card-soft rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">{row.label}</span>
                  <span className="rounded-md bg-secondary px-2.5 py-1 font-mono text-xs font-medium">{row.value}</span>
                </div>
                {row.interpretation && <p className="mt-2 text-sm text-muted-foreground">{row.interpretation}</p>}
              </div>
            ))}
          </div>
        </Section></>
      )}

      {/* Question analysis */}
      {c.question_analysis && (c.question_analysis.points || []).length > 0 && (
        <><PaisleyDivider />
        <Section title={c.question_analysis.title || s.why} testid="section-why">
          <div className="mt-3 space-y-3">
            {c.question_analysis.points.map((p, i) => (
              <div key={i} className="border-l-2 border-gold/40 pl-4">
                <div className="text-sm font-medium">{p.label}</div>
                {p.fact && <div className="text-xs font-mono text-muted-foreground">{p.fact}</div>}
                {p.interpretation && <p className="mt-1 text-[15px] text-muted-foreground">{p.interpretation}</p>}
              </div>
            ))}
          </div>
          {c.question_analysis.conclusion && <p className="mt-4 rounded-xl bg-secondary p-4 text-[15px]">{c.question_analysis.conclusion}</p>}
        </Section></>
      )}

      {/* Dasha */}
      {(c.mahadasha?.lord || c.antardasha?.lord) && (
        <Section icon={Clock} title={s.maha} testid="section-dasha">
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {c.mahadasha?.lord && <DashaCard label={s.maha} lord={c.mahadasha.lord} range={`${c.mahadasha.from} – ${c.mahadasha.to}`} text={c.mahadasha.why} />}
            {c.antardasha?.lord && <DashaCard label={s.antar} lord={c.antardasha.lord} range={`${c.antardasha.from} – ${c.antardasha.to}`} text={c.antardasha.activates} />}
          </div>
        </Section>
      )}

      {/* Dasha timeline */}
      {(c.dasha_timeline || []).length > 0 && (
        <Section title={s.dashaTl} testid="section-dasha-timeline">
          <div className="relative mt-4 space-y-5 border-l-2 border-gold/30 pl-6">
            {c.dasha_timeline.map((d, i) => (
              <div key={i} className="relative">
                <span className="absolute -left-[31px] top-1 h-3.5 w-3.5 rounded-full bg-gold ring-4 ring-background" />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-lg font-semibold">{d.period}</span>
                  <span className="rounded-md bg-secondary px-2 py-0.5 text-xs">{d.range}</span>
                </div>
                {d.houses && d.houses !== "—" && <div className="text-xs text-muted-foreground">{s.rules}: {d.houses}</div>}
                {d.interpretation && <p className="mt-1 text-sm text-muted-foreground">{d.interpretation}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Transits */}
      {(c.transits || []).length > 0 && (
        <><PaisleyDivider />
        <Section icon={Orbit} title={s.transitsT} testid="section-transits">
          <div className="mt-3 space-y-2.5">
            {c.transits.map((t, i) => (
              <div key={i} className="card-soft rounded-xl p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-foreground px-3 py-1 text-xs font-medium text-background">{t.planet}</span>
                  <span className="font-mono text-xs text-muted-foreground">{t.position}</span>
                </div>
                {t.interpretation && <p className="mt-2 text-sm text-muted-foreground">{t.interpretation}</p>}
              </div>
            ))}
          </div>
        </Section></>
      )}

      {/* Periods */}
      {c.periods && (c.periods.supportive?.why || c.periods.demanding?.why) && (
        <Section icon={TrendingUp} title={s.periodsT} testid="section-periods">
          <div className="mt-3 space-y-3">
            <PeriodCard icon={CheckCircle2} tone="good" label={s.supportive} p={c.periods.supportive} />
            <PeriodCard icon={Scale} tone="neutral" label={s.transition} p={c.periods.transition} />
            <PeriodCard icon={AlertTriangle} tone="warn" label={s.demanding} p={c.periods.demanding} />
          </div>
        </Section>
      )}

      {/* 12-36 month timeline */}
      {(c.timeline || []).length > 0 && (
        <><PaisleyDivider />
        <Section title={s.timeline} testid="section-timeline">
          <div className="relative mt-5 space-y-6 border-l-2 border-gold/30 pl-6">
            {c.timeline.map((t, i) => (
              <div key={i} className="relative">
                <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-gold ring-4 ring-background"><span className="h-1.5 w-1.5 rounded-full bg-background" /></span>
                <div className="text-sm font-medium gold-text">{t.window}</div>
                <div className="font-display text-xl font-semibold">{t.title}</div>
                <p className="mt-1 text-[15px] leading-relaxed text-muted-foreground">{t.interpretation}</p>
                {t.factors && <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">Factors: </span>{t.factors}</p>}
                {t.represents && <p className="mt-1 text-xs italic text-muted-foreground">{t.represents}</p>}
              </div>
            ))}
          </div>
        </Section></>
      )}

      {/* What could change */}
      {(c.what_could_change || []).length > 0 && (
        <Section title={s.change} testid="section-change">
          <ul className="mt-3 space-y-2">
            {c.what_could_change.map((m, i) => <li key={i} className="flex gap-3 text-[15px] text-muted-foreground"><span className="gold-text">·</span> {m}</li>)}
          </ul>
        </Section>
      )}

      {/* Meaning (simple) */}
      {(c.meaning || []).length > 0 && (
        <Section title={s.meaning} testid="section-meaning">
          <ul className="mt-3 space-y-2.5">{c.meaning.map((m, i) => <li key={i} className="flex gap-3 text-[15px] text-muted-foreground"><span className="gold-text">·</span> {m}</li>)}</ul>
        </Section>
      )}

      {/* Reflection */}
      {(c.reflection || []).length > 0 && (
        <Section title={s.reflection} testid="section-reflection">
          <div className="mt-3 space-y-3">{c.reflection.map((m, i) => <div key={i} className="flex gap-3 rounded-2xl bg-gold-soft/50 p-4 text-[15px]"><Quote className="h-4 w-4 shrink-0 gold-text" /> {m}</div>)}</div>
        </Section>
      )}

      {/* Technical */}
      {(c.technical || []).length > 0 && (
        <Section title={s.technical} testid="section-technical">
          <Accordion type="single" collapsible className="mt-3">
            {c.technical.map((t, i) => (
              <AccordionItem key={i} value={`t${i}`} className="card-soft mb-2 rounded-xl border-none px-4">
                <AccordionTrigger data-testid={`explain-technical-${i}`} className="text-sm hover:no-underline">{t.title}</AccordionTrigger>
                <AccordionContent>
                  {t.plain && <p className="text-sm text-muted-foreground">{t.plain}</p>}
                  {t.technical && <p className="mt-2 break-words font-mono text-xs text-muted-foreground">{t.technical}</p>}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      )}

      {/* Summary points */}
      {(c.summary_points || []).length > 0 && (
        <Section title={s.summary} testid="section-summary">
          <div className="mt-3 space-y-2">
            {c.summary_points.map((p, i) => (
              <div key={i} className="flex gap-3 text-[15px]"><span className="font-display font-semibold gold-text">{i + 1}</span> <span className="text-muted-foreground">{p}</span></div>
            ))}
          </div>
        </Section>
      )}

      <div className="mt-6 space-y-1.5 rounded-2xl bg-muted p-4">
        {r.disclaimers.map((d, i) => <p key={i} className="text-xs text-muted-foreground">{d}</p>)}
      </div>

      {(r.cross_sell || []).length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-2xl font-semibold tracking-tight">{s.next}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {r.cross_sell.map((cs) => (
              <button key={cs.tool_id} data-testid={`crosssell-${cs.tool_id}`} onClick={() => crossSell(cs)} className="card-soft flex items-center justify-between gap-3 rounded-2xl p-5 text-left btn-lift">
                <span><span className="text-2xl">{cs.emoji}</span><div className="mt-2 font-medium">{cs.question}</div><div className="text-sm gold-text">{s.from}{cs.price}</div></span>
                <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} share={share} />
    </div>
  );
}

function Section({ title, children, testid, icon: Icon }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <h2 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight">
        {Icon && <Icon className="h-5 w-5 gold-text" />} {title}
      </h2>
      {children}
    </section>
  );
}

function Mini({ label, text, tone }) {
  const c = tone === "good" ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800";
  return <div className={`rounded-xl p-3 ${c}`}><div className="text-xs font-semibold uppercase tracking-wide">{label}</div><p className="mt-1 text-sm">{text}</p></div>;
}

function DashaCard({ label, lord, range, text }) {
  return (
    <div className="card-soft rounded-2xl p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-2"><span className="font-display text-2xl font-semibold gold-text">{lord}</span><span className="rounded-md bg-secondary px-2 py-0.5 text-xs">{range}</span></div>
      {text && <p className="mt-2 text-sm text-muted-foreground">{text}</p>}
    </div>
  );
}

function PeriodCard({ icon: Icon, tone, label, p }) {
  if (!p || !p.why) return null;
  const c = tone === "good" ? "text-green-700" : tone === "warn" ? "text-amber-700" : "text-muted-foreground";
  return (
    <div className="card-soft rounded-2xl p-5">
      <div className={`flex items-center gap-2 text-sm font-semibold ${c}`}><Icon className="h-4 w-4" /> {label}{p.range ? ` · ${p.range}` : ""}</div>
      <p className="mt-1.5 text-[15px] text-muted-foreground">{p.why}</p>
    </div>
  );
}

function ShareDialog({ open, onOpenChange, share }) {
  if (!share) return null;
  const text = `${share.first_name}'s ${share.category} reading on Jaanlo — "${share.theme_title}". ${share.insight}`;
  const doShare = async () => {
    try {
      if (navigator.share) await navigator.share({ title: "Jaanlo", text, url: window.location.origin });
      else { await navigator.clipboard.writeText(text + " " + window.location.origin); toast.success("Copied to clipboard"); }
    } catch { /* cancelled */ }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-display">Found something interesting?</DialogTitle></DialogHeader>
        <div className="relative overflow-hidden rounded-2xl bg-foreground p-7 text-background" data-testid="share-card">
          <LotusMark className="absolute -right-4 -top-4 h-24 w-24 text-background/10" />
          <div className="flex items-center gap-2 text-sm"><LotusMark className="h-4 w-4 gold-text" /> Jaanlo</div>
          <div className="mt-6 text-xs uppercase tracking-wider text-background/60">{share.first_name} • {share.category}</div>
          <div className="mt-1 font-display text-2xl font-semibold gold-text">{share.theme_title}</div>
          <p className="mt-3 text-sm text-background/80">{share.insight}</p>
          <div className="mt-6 rounded-full bg-gold px-4 py-2 text-center text-sm font-medium text-foreground">{share.cta}</div>
        </div>
        <Button data-testid="do-share" onClick={doShare} className="w-full rounded-full bg-foreground text-background hover:bg-foreground">Share my result</Button>
        <p className="text-center text-xs text-muted-foreground">Your birth details and private information are never shared.</p>
      </DialogContent>
    </Dialog>
  );
}
